import { CircularProgress, WithStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./ManageReportsView.styles";
import { useTranslation } from "react-i18next";
import * as SingleIsinReportAction from "../../../stores/reports/singleIsin/SingleIsinReportAction";
import { useDispatch, useSelector } from "react-redux";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { convertDateStringToNumber, today } from "../../../utilities/dateTimeUtils";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { CreateReportDialog, IReportData } from "../myAccount/reports/CreateReportDialog";
import { DATE_FORMAT } from "../../../constants/TIME";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { searchCaseInsensitive } from "../../../utilities/generalUtils";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import { IsinTableComponent } from "../myAccount/reports/IsinsTableComponent";
import SingleIsinReportModel from "../../../models/reports/singleIsin/SingleIsinReportModel";

export interface IManageReportsViewProps extends WithStyles<typeof styles> {}

const ManageReportsViewPure: React.FunctionComponent<IManageReportsViewProps> = ({
    classes
}: IManageReportsViewProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(SingleIsinReportAction.requestAllReports());
    }, [dispatch]);

    const [isOnBehalfDialogShown, setIsOnBehalfDialogShown] = useState<boolean>(false);

    const reportFromState = useSelector(state => state.singleIsinsReports?.allReports);
    const isRequesting = useSelector(state => selectRequesting(state, [SingleIsinReportAction.REQUEST_ALL_REPORTS]));
    let reports: IUniversalTableData;

    if (reportFromState) {
        reports = Object.values(reportFromState).map((report: SingleIsinReportModel) => ({
            id: report.id,
            email: report.email,
            date: report.date,
            isins: report.isins,
            created: report.created,
            price: report.price,
            discount: report.discount,
            totalPrice: report.totalPrice,
            state: report.state,
            reportLink: report.reportLink
        }));
    }

    const handleEditFinished = (index, newValue) => {
        const updatedReport = {
            discount: parseFloat(newValue?.discount)
        };
        setIsOnBehalfDialogShown(false);
        dispatch(SingleIsinReportAction.updateReport(reports[index]?.id, updatedReport))
            .then(() => {
                dispatch(SingleIsinReportAction.requestAllReports());
                enqueueSnackbar("Report has been updated", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Report edit failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: "Report ID",
            field: "id",
            editable: "never"
        },
        {
            type: "text",
            title: "User",
            field: "email",
            editable: "never"
        },
        {
            type: "date",
            title: "Date",
            field: "date",
            editable: "never"
        },
        {
            type: "component",
            title: "Isins",
            component: rowData => <IsinTableComponent isins={rowData?.isins} state={rowData?.state} />,
            searchFunc: (term, rowData) => rowData?.isins?.some(isin => searchCaseInsensitive(term, isin?.isin))
        },
        {
            type: "dateTime",
            title: t("general.createdOn"),
            field: "created",
            editable: "never"
        },
        {
            type: "price",
            title: "Subtotal",
            field: "price",
            editable: "never"
        },        
        {
            type: "price",
            title: "Discount Amount",
            field: "discount",
            validate: rowData => {
                const discountNumber = parseFloat(rowData?.discount);
                return !isNaN(discountNumber)
                    ? ((discountNumber <= rowData?.totalPrice && discountNumber >= 0) ? true : { isValid: false, helperText: "Discount must be positive and can't be higher than the total amount" })
                    : { isValid: false, helperText: "Please enter a valid number" };
            }
        },
        {
            type: "price",
            title: "Total amount (excl. VAT)",
            field: "totalPrice",
            editable: "never"
        },
        {
            type: "link",
            title: "Report",
            field: "reportLink",
            linkField: "reportLink",
            linkTitle: "Download",
            fieldIfLinkNotAvailable: "state",
            editable: "never"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "reports.write"
                }
            ]
        }
    ];

    const handleCreateNewReport = (newReport: IReportData) => {
        setIsOnBehalfDialogShown(false);
        const reportToSend = {
            ...newReport,
            isins: newReport.isins?.map(i => ({ isin: i }))
        };
        dispatch(SingleIsinReportAction.createReportOnBehalf(reportToSend))
            .then(() => {
                dispatch(SingleIsinReportAction.requestAllReports());
                enqueueSnackbar("Report has been created", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Report creation failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const defaultDate = convertDateStringToNumber(today().subtract(1, "days").format(DATE_FORMAT));

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Manage reports", href: "/my-account/manage-reports" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3" gutterBottom>
                    Manage reports
                </Typography>
            </div>
            <PermissionGuard scope="reports.write">
                <BlueButton style={{ margin: 0, marginTop: 20 }} onClick={() => setIsOnBehalfDialogShown(true)}>
                    Request ISIN report on behalf
                </BlueButton>
            </PermissionGuard>
            <div className={classes.gap}></div>
            {isRequesting ? (
                <div className={classes.center}>
                    <CircularProgress color="inherit" className={classes.center} />
                </div>
            ) : (
                <UniversalTable columns={columns} data={reports} isLoading={isRequesting} searchable />
            )}
            {isOnBehalfDialogShown && (
                <CreateReportDialog
                    date={defaultDate}
                    dialogType="createReportOnBehalf"
                    onConfirm={handleCreateNewReport}
                    onClose={() => setIsOnBehalfDialogShown(false)}
                />
            )}
        </CenteredContainer>
    );
};

export const ManageReportsView = withStyles(styles)(ManageReportsViewPure);
